import { Result } from "antd";
import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import LoadingOverlay from "../components/atoms/LoadingOverlay";
import DataSyndicationSourceSubscriptionList from "../components/molecules/DataSyndicationSourceSubscriptionList";
import { createDataSyndicationSourceSubscriptionRequest, getDataSyndicationSourceSubscriptionListRequest } from "../store/dataSyndicationSource/actions";

interface SubscriptionListResult {
    Items: IDataSyndicationSourceSubscription[];
}

interface DataSyndicationSourceSubscriptionProps {
    sourceId?: string;
    readOnly?: boolean;
    subscriptionListResult: SubscriptionListResult;
    getDataSyndicationSourceSubscriptionListAction: {
        status: "PENDING" | "SUCCESS" | "FAILED";
        error?: string;
    };
    newSourceSubscriptionAction: Record<string, Record<string, string>>;
    getDataSyndicationSourceSubscriptionList: (sourceId: string) => void;
    createDataSyndicationSourceSubscription: (sourceId: string, subscription: IDataSyndicationSourceSubscription) => void;
}

const DataSyndicationSourceSubscription: React.FC<DataSyndicationSourceSubscriptionProps> = ({
    sourceId,
    readOnly = false,
    subscriptionListResult,
    getDataSyndicationSourceSubscriptionListAction,
    newSourceSubscriptionAction,
    getDataSyndicationSourceSubscriptionList,
    createDataSyndicationSourceSubscription,
}) => {
    useEffect(() => {
        if (sourceId) {
            getDataSyndicationSourceSubscriptionList(sourceId);
        }
    }, [sourceId, getDataSyndicationSourceSubscriptionList]);

    const handleSubscriptionSave = useCallback(
        (subscription: IDataSyndicationSourceSubscription) => {
            if (sourceId) {
                createDataSyndicationSourceSubscription(sourceId, subscription);
            }
        },
        [sourceId, createDataSyndicationSourceSubscription],
    );

    const isLoading = getDataSyndicationSourceSubscriptionListAction.status === "PENDING";
    const isError = getDataSyndicationSourceSubscriptionListAction.status === "FAILED";

    if (isError) {
        return (
            <Result
                status="error"
                title="Failed to load subscriptions"
                subTitle={getDataSyndicationSourceSubscriptionListAction.error || "An error occurred while loading the subscriptions."}
                extra={[
                    <button key="retry" onClick={() => sourceId && getDataSyndicationSourceSubscriptionList(sourceId)} className="ant-btn ant-btn-primary">
                        Try Again
                    </button>,
                ]}
            />
        );
    }

    return (
        <div style={{ position: "relative" }}>
            <LoadingOverlay busy={isLoading} message="Loading subscriptions..." />
            <DataSyndicationSourceSubscriptionList
                accounts={subscriptionListResult.Items}
                onSubscriptionSave={handleSubscriptionSave}
                readOnly={readOnly}
                subscriptionAction={newSourceSubscriptionAction}
            />
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    subscriptionListResult: state.dataSyndicationSource.subscriptionListResult,
    getDataSyndicationSourceSubscriptionListAction: state.dataSyndicationSource.getDataSyndicationSourceSubscriptionListAction,
    newSourceSubscriptionAction: state.dataSyndicationSource.newSourceSubscriptionAction,
});

const mapDispatchToProps = (dispatch: any) => ({
    getDataSyndicationSourceSubscriptionList: (sourceId: string) => dispatch(getDataSyndicationSourceSubscriptionListRequest(sourceId)),
    createDataSyndicationSourceSubscription: (sourceId: string, subscription: IDataSyndicationSourceSubscription) =>
        dispatch(createDataSyndicationSourceSubscriptionRequest(sourceId, subscription)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DataSyndicationSourceSubscription);
